import { omit } from 'lodash-es'
import {
  createContext,
  startTransition,
  useCallback,
  useContext,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { Platform, View, type ViewProps } from 'react-native'
import Animated from 'react-native-reanimated'
import { nativeEnteringAnimations, nativeExitingAnimations } from '~/components/animations/blob'
import { Badge } from '~/components/ui/badge'
import { AnimatedPressable } from '~/components/ui/pressable'
import { Text } from '~/components/ui/text'
import { cn } from '~/lib/utils'

export type TabAnimationOrigin = 'top-left' | 'top-center' | 'top-right'

export interface TabItem<TabId extends string> {
  id: TabId
  label: string
  content: ReactNode | ((props: { active: boolean }) => ReactNode)
  count?: number | string
  contentClassName?: string
  animationOrigin?: TabAnimationOrigin
}

interface TabsContextValue<TabId extends string> {
  tabs: TabItem<TabId>[]
  activeTab: TabId
  activeTabIndex: number
  internalCounts: Record<TabId, number | string | undefined>
  animateToIndex: (index: number) => void
  setTabCount: (tabId: string, count: number | string | undefined) => void
}

const TabsContext = createContext<TabsContextValue<string> | null>(null)

function useTabsContext<TabId extends string>(): TabsContextValue<TabId> {
  const context = useContext(TabsContext)

  if (!context) {
    // eslint-disable-next-line i18next/no-literal-string
    throw new Error('TabBar and TabContent must be used within a Tabs provider')
  }

  return context as TabsContextValue<TabId>
}

function getTabCounts<TabId extends string>(tabs: TabItem<TabId>[]) {
  return tabs.reduce(
    (acc, tab) => {
      if (typeof tab.count !== 'undefined') {
        acc[tab.id] = tab.count
      }

      return acc
    },
    {} as Record<TabId, number | string | undefined>
  )
}

interface TabsProps<TabId extends string> {
  tabs: TabItem<TabId>[]
  activeTab: TabId
  setActiveTab: (tabId: TabId) => void
  children: ReactNode
}

export const Tabs = <TabId extends string>({
  tabs,
  activeTab,
  setActiveTab,
  children,
}: TabsProps<TabId>) => {
  const [internalCounts, setInternalCounts] = useState(getTabCounts(tabs))

  const activeTabIndex = useMemo(
    () =>
      Math.max(
        0,
        tabs.findIndex((tab) => tab.id === activeTab)
      ),
    [tabs, activeTab]
  )

  const controlledCounts = useRef<Set<string>>(new Set())

  useEffect(() => {
    const setCounts = omit(getTabCounts(tabs), Array.from(controlledCounts.current))

    if (Object.keys(setCounts).length > 0) {
      setInternalCounts(setCounts as Record<TabId, number | string | undefined>)
    }
  }, [tabs])

  const tabIdByIndex = useMemo(() => tabs.map((t) => t.id), [tabs])

  const animateToIndex = useCallback(
    (index: number) => {
      startTransition(() => {
        setActiveTab?.(tabIdByIndex[index])
      })
    },
    [setActiveTab, tabIdByIndex]
  )

  const setTabCount = useCallback((tabId: string, count: number | string | undefined) => {
    controlledCounts.current.add(tabId)

    startTransition(() => {
      setInternalCounts((prev) => {
        if (prev[tabId as TabId] === count) {
          return prev
        }

        return { ...prev, [tabId]: count }
      })
    })
  }, [])

  const contextValue = useMemo(
    (): TabsContextValue<TabId> => ({
      tabs,
      activeTab,
      activeTabIndex,
      internalCounts,
      animateToIndex,
      setTabCount,
    }),
    [tabs, activeTab, activeTabIndex, internalCounts, animateToIndex, setTabCount]
  )

  return (
    <TabsContext.Provider value={contextValue as TabsContextValue<string>}>
      {children}
    </TabsContext.Provider>
  )
}

interface TabBarProps {
  className?: string
  tabButtonClassName?: string
  tabButtonTextClassName?: string
  tabButtonCountClassName?: string
  tabButtonCountTextClassName?: string
  onPress?: (tabId: string) => void
  showZeroCountBadges?: boolean
}

export function TabBar<TabId extends string>({
  className,
  tabButtonClassName,
  tabButtonTextClassName,
  tabButtonCountClassName,
  tabButtonCountTextClassName,
  onPress,
  showZeroCountBadges = false,
}: TabBarProps) {
  const { tabs, activeTab, internalCounts, animateToIndex } = useTabsContext<TabId>()

  const deferredActiveTab = useDeferredValue(activeTab)
  const deferredInternalCounts = useDeferredValue(internalCounts)

  if (!tabs || tabs.length === 0) {
    return null
  }

  return (
    <View className={cn('w-full flex-row gap-2', className)} accessibilityRole="tablist">
      {tabs.map((tab, index) => {
        const displayedCount =
          deferredInternalCounts[tab.id as TabId] ?? (tab as TabItem<TabId>).count

        const isActive = deferredActiveTab === tab.id

        return (
          <AnimatedPressable
            key={tab.id}
            testID="tab-trigger"
            onPress={() => {
              animateToIndex(index)
              onPress?.(tab.id)
            }}
            accessibilityRole="tab"
            accessibilityState={{ selected: deferredActiveTab === tab.id }}
            aria-selected={isActive}
            className={cn(
              'group/tab mb-1.5 flex-auto items-center justify-center rounded-content-inner border-0 bg-muted px-2 pb-3 pt-4 transition-all delay-0 duration-100 ease-out aria-selected:mb-0 aria-selected:translate-y-1.5 aria-selected:rounded-b-none aria-selected:bg-white aria-selected:pb-4 aria-selected:duration-150 aria-selected:ease-in sm:px-3',
              tabButtonClassName
            )}>
            <View className="flex-row items-center justify-center">
              <Text
                className={cn(
                  'text-pretty text-center font-normal text-base/relaxed text-primary transition-[color,font-weight] duration-150 group-aria-selected/tab:font-medium group-aria-selected/tab:text-accent',
                  tabButtonTextClassName
                )}>
                {tab.label}
              </Text>
              {(!!displayedCount || (showZeroCountBadges && displayedCount === 0)) && (
                <Badge
                  variant={isActive ? 'primary' : 'secondary'}
                  size="xs"
                  className={cn(
                    'ml-2 h-6 min-w-6 items-center justify-center group-aria-selected/tab:bg-accent',
                    tabButtonCountClassName
                  )}
                  textClassName={cn(
                    'text-center font-medium text-xxs/none text-white sm:text-sm/none',
                    tabButtonCountTextClassName
                  )}>
                  {String(displayedCount)}
                </Badge>
              )}
            </View>
          </AnimatedPressable>
        )
      })}
    </View>
  )
}

const webAnimationClasses: Record<TabAnimationOrigin, string> = {
  'top-left': 'animate-blob-in-top-left',
  'top-center': 'animate-blob-in-top-center',
  'top-right': 'animate-blob-in-top-right',
}

interface TabContentProps {
  className?: string
  tabContentClassName?: string
  onLayout?: ViewProps['onLayout']
  ref?: React.Ref<View>
}

const isWeb = Platform.OS === 'web'

export function TabContent<TabId extends string>({
  className,
  tabContentClassName,
  onLayout,
  ref,
}: TabContentProps) {
  const { tabs, activeTab } = useTabsContext<TabId>()
  const [height, setHeight] = useState<number>(0)
  const deferredHeight = useDeferredValue(height)

  const [animationKey, setAnimationKey] = useState(0)
  const previousActiveTab = useRef(activeTab)

  useEffect(() => {
    if (previousActiveTab.current !== activeTab) {
      setAnimationKey((prev) => prev + 1)
      previousActiveTab.current = activeTab
    }
  }, [activeTab])

  if (!tabs || tabs.length === 0) {
    return null
  }

  return (
    <View
      ref={ref}
      className={cn('relative', className)}
      style={{ height: deferredHeight }}
      onLayout={onLayout}>
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id
        const origin = tab.animationOrigin ?? 'top-center'

        if (isActive) {
          return (
            <Animated.View
              key={`${tab.id}-${animationKey}`}
              onLayout={(event) => setHeight(event.nativeEvent.layout.height)}
              aria-selected
              className={cn(
                tabContentClassName,
                tab.contentClassName,
                isWeb && webAnimationClasses[origin]
              )}
              entering={isWeb ? undefined : nativeEnteringAnimations[origin]}
              exiting={isWeb ? undefined : nativeExitingAnimations[origin]}>
              {typeof tab.content === 'function' ? tab.content({ active: isActive }) : tab.content}
            </Animated.View>
          )
        }

        return (
          <View
            key={tab.id}
            aria-selected={isActive}
            className={cn('pointer-events-none absolute h-0 w-0 overflow-hidden opacity-0')}
            aria-hidden>
            {typeof tab.content === 'function' ? tab.content({ active: isActive }) : tab.content}
          </View>
        )
      })}
    </View>
  )
}

export function useSetTabCount(tabId: string, count: number | string | undefined) {
  const context = useContext(TabsContext)

  useEffect(() => {
    if (context) {
      startTransition(() => {
        context.setTabCount(tabId, count)
      })
    }
  }, [context, tabId, count])
}

export function getTabCount(count: number | string) {
  if (typeof count === 'number') {
    return count > 99 ? '99+' : count
  }

  return count
}
